<script lang="ts">
    import Portal from "svelte-portal"
    import { createEventDispatcher, text } from "svelte/internal"
    import Popup from "../Popup.svelte"
    import PopupBanner from "../PopupBanner.svelte"
    import PopupButtons from "../PopupButtons.svelte"
    import PopupExit from "../PopupExit.svelte"
    import PopupHeading from "../PopupHeading.svelte"
    import PopupIcon from "../PopupIcon.svelte"
    import PopupIconDivider from "../PopupIconDivider.svelte"
    import PopupParagraph from "../PopupParagraph.svelte"
    import {
        DisplayWord,
        PrincipleClass,
        Sentence,
        fromRange,
        getRenderItemRange,
        weigh,
    } from "@circleofa/acim-engine"
    import { acim } from "@utils/acim"
    import {
        RedrawEventDispatcher,
        attachHighlighterListener,
        contextScrollContainerClass,
    } from "@utils/highlights/directive-logic/highlights-directive"
    import {
        ContextMenuViewportSelection,
        FocusSetting,
        HighlightSettingLimiter,
        type PopWords,
    } from "@utils/highlights/directive-logic/highlight-listener-permissions"
    import { scrollLockClass } from "@utils/highlights/directive-logic/scroll-locker"
    import { writable } from "svelte/store"
    import ContextMenu from "@components/utilities/context-menu/ContextMenu.svelte"
    import ContextMenuIcon from "@components/utilities/context-menu/ContextMenuIcon.svelte"
    import ContextMenuLink from "@components/utilities/context-menu/ContextMenuLink.svelte"
    import { selectedTab, selectTab } from "@store/pageTracker"
    import { goto } from "$app/navigation"
    import { setKeyedPagePopWords } from "../../../routes/book/[notation]/KeyedPage.svelte"
    import { htmlRenderItems } from "@utils/reading/render"

    export let path: string
    const [first, last] = fromRange(path, acim)
    // Get display words
    const renderItems = getRenderItemRange(first.containingRenderItem, last.containingRenderItem)

    const renderable = renderItems[0].renderParent

    const title =
        renderable instanceof PrincipleClass
            ? "Miracle Principle " + renderable.linkName[0]
            : renderable.linkName[1]

    // Only
    const wholeRange =
        renderItems[0].firstWord === first.firstWord &&
        renderItems[renderItems.length - 1].lastWord === last.lastWord
    const popRange: PopWords = wholeRange ? [] : [[first.firstWord, last.lastWord]]

    const redrawEventDispatcher = new RedrawEventDispatcher()
    let readerWidth: number = 0
    $: redrawEventDispatcher.resize(readerWidth)

    let ready = writable(false)

    const dispatch = createEventDispatcher()

    let showContextMenu = false

    const go = () => {
        dispatch("exit")
        setKeyedPagePopWords(popRange)
        goto(`/book/${renderable.path}#${path}`)
    }
</script>

<Portal>
    <Popup on:introend={() => ($ready = true)}>
        <PopupExit on:exit={() => dispatch("exit")} />
        <PopupBanner>
            <PopupIconDivider>
                {#if showContextMenu}
                    {#if showContextMenu}
                        <ContextMenu on:exit={() => (showContextMenu = false)}>
                            {#if $selectedTab}
                                <ContextMenuLink icon="popout" cb={go}>
                                    <div slot="icon">
                                        {#if $selectedTab.emoji}
                                            {$selectedTab.emoji}
                                        {:else}
                                            <ContextMenuIcon icon="tab" />
                                        {/if}
                                    </div>
                                    Open in {$selectedTab.displayName}
                                </ContextMenuLink>
                            {/if}
                            <ContextMenuLink
                                icon="popout"
                                cb={() => {
                                    selectTab("abandoned")
                                    go()
                                }}>Browse</ContextMenuLink
                            >
                        </ContextMenu>
                    {/if}
                {/if}
                <div slot="icon">
                    <span
                        ><a
                            class="link underline"
                            on:click|preventDefault={() => {
                                if (!$selectedTab) {
                                    go()
                                } else {
                                    showContextMenu = true
                                }
                            }}
                            href="/book/{renderable.path}#{path}">{renderable.path}</a
                        ></span
                    >
                </div>
                <PopupHeading>
                    <slot name="title">{title}</slot>
                </PopupHeading>
            </PopupIconDivider>
        </PopupBanner>
        <div class="relative -m-4 p-4">
            <div class="{contextScrollContainerClass} relative">
                <div class="relative">
                    <div
                        bind:clientWidth={readerWidth}
                        class="reader minireader z-1"
                        use:attachHighlighterListener={{
                            redrawEventDispatcher,
                            settings: {
                                emphasizeHighlight: "faded",
                                editHighlights: HighlightSettingLimiter.NONE,
                                createHighlights: false,
                                popWords: popRange,
                                viewportSelection: ContextMenuViewportSelection.VIEWPORT,
                            },
                            waitUntil: ready,
                        }}
                    >
                        {@html htmlRenderItems(renderItems)}
                    </div>
                </div>
            </div>
        </div>
    </Popup>
</Portal>
